import { AppUrls } from '@/urls';
import React, { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import Spin from '@/components/Spin';
import CompanyDetailForm from '@/components/Form/AgentRegisterForm/CompanyDetailForm';
import PersonInChargeForm from '@/components/Form/AgentRegisterForm/PersonInChargeForm';
import AccountantForm from '@/components/Form/AgentRegisterForm/AccountantForm';
import EmergencyContractForm from '@/components/Form/AgentRegisterForm/EmergencyContractForm';
import CompanyLicenseForm from '@/components/Form/AgentRegisterForm/CompanyLicenseForm';
import { AgentType } from '@/constants';
import { apiSdk } from '@/httpClient';
import { FileUploadResponse, RegisterAgentModel } from '@/__generated';
import { ModalUtils } from '@/contexts/ModalWrapper';
import { useTranslation } from 'react-i18next';
import { UNKNOWN_ERROR_MESSAGE } from '@/config';
import TermsAndConditionsModal from '@/components/App/Modals/TermsAndConditionsModal';
import { Helmet } from 'react-helmet';
import LoginDetailForm from '@/components/Form/AgentRegisterForm/LoginDetailForm';

export default function AgentRegisterPage() {
  const [loading, setLoading] = useState(false);
  const [displayTermsModal, setDisplayTermsModal] = React.useState<boolean>(true);
  const [error, setError] = useState<any>();
  const { t } = useTranslation();
  const history = useHistory();
  const {
    register,
    handleSubmit,
    watch,
    setFocus,
    control,
    formState: { errors },
  } = useForm<any>({
    defaultValues: {
      haveLicense: 'Yes',
      agentType: { value: AgentType.RetailerTravelAgency, label: AgentType.__getValue(AgentType.RetailerTravelAgency) },
    },
  });
  React.useEffect(() => {
    const firstError = Object.keys(errors).reduce((field: any, a) => {
      return !!errors[field] ? field : a;
    }, null);

    if (firstError) {
      setFocus(firstError);
    }
  }, [errors, setFocus]);

  const uploadLicenses = async (files: FileList) => {
    let fileResponses: FileUploadResponse[] = [];
    for (var i = 0; i < files.length; i++) {
      fileResponses.push(await apiSdk.AgentRegistrationService.uploadLicense({ formFile: files[i] }));
    }
    return fileResponses;
  };
  const registerAgent = (data: RegisterAgentModel) => {
    apiSdk.AgentRegistrationService.create({ body: data })
      .then(res => {
        if (res.success) {
          ModalUtils.success({
            content: t('AGENT_REGISTER_SUCCESS'),
            onClose: () => {
              history.push(AppUrls.Login);
            },
          });
        } else {
          ModalUtils.error({
            content: t(res.message || UNKNOWN_ERROR_MESSAGE),
          });
        }
      })
      .catch(() => {
        ModalUtils.error({
          content: t(UNKNOWN_ERROR_MESSAGE),
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const onSubmit = async (data: any) => {
    setLoading(true);
    setError('');
    let fileIds: FileUploadResponse[] = [];
    let { licenseFiles, haveLicense, country, agentType, ...values } = data;
    try {
      if (haveLicense === 'Yes' && licenseFiles?.length) {
        fileIds = await uploadLicenses(licenseFiles);
        if (fileIds?.some(x => !x.success)) {
          throw new Error();
        }
      }
      Object.keys(values).forEach(key => {
        if (values[key] === '' || values[key] == null) {
          delete values[key];
        }
      });

      registerAgent({
        ...values,
        agentType: agentType?.value,
        countryCode: country?.value,
        companySize: data.companySize?.value,
        licenseFileIds: fileIds?.map(x => x.fileId || '') || undefined,
      });
    } catch (error) {
      setLoading(false);
      ModalUtils.error({
        title: t('Error'),
        content: t('AGENT_UPLOAD_LICENSE_ERROR'),
      });
    }
  };

  return (
    <section className="layout-pt-sm layout-pb-sm bg-blue-2">
      <Helmet>
        <title>Agent register</title>
      </Helmet>
      {displayTermsModal ? (
        <TermsAndConditionsModal
          onClose={() => {
            setDisplayTermsModal(false);
          }}
        />
      ) : null}
      <div className="container">
        <div className="row justify-center">
          <div className="col-xl-10 col-lg-12 col-md-12">
            <Spin loading={loading}>
              <div className="px-50 py-50 sm:px-20 sm:py-20 bg-white shadow-4 rounded-4">
                {error ? <div className="text-red-1 fw-bold text-18">{error}</div> : null}

                <form className="row y-gap-20" onSubmit={handleSubmit(onSubmit)}>
                  <div className="col-12">
                    <h1 className="text-22 fw-500">{t('agentRegister_Welcome')} </h1>
                    <p className="mt-10">
                      {t('agentRegister_HaveAccount')}
                      <Link to={AppUrls.Login} className="text-blue-1 ml-5">
                        {t('base.Login')}
                      </Link>
                    </p>
                  </div>

                  <CompanyDetailForm control={control} errors={errors} register={register} />
                  <LoginDetailForm control={control} errors={errors} register={register} />
                  <PersonInChargeForm errors={errors} register={register} />
                  <EmergencyContractForm errors={errors} register={register} />
                  <AccountantForm errors={errors} register={register} />
                  <CompanyLicenseForm errors={errors} register={register} watch={watch} />
                  {/* button */}
                  <div className="col-12 layout-pt-md">
                    <button type="submit" className="button py-20 -dark-1 bg-blue-1 text-white w-100">
                      {t('base.SignUp')} <div className="icon-arrow-top-right ml-15" />
                    </button>
                  </div>
                </form>
              </div>
            </Spin>
          </div>
        </div>
      </div>
    </section>
  );
}
